<template>
  <div class="modal">
    <div class="modal__header">
      <h3 class="modal__title">{{ $t("callback.callback") }}</h3>
      <p class="modal__text">
        {{ $t("callback.leaveRequest1") }} <br/>
        {{ $t("callback.leaveRequest2") }}
      </p>
    </div>

    <form @submit.prevent="submitForm" class="form">
      <input
        class="input__field"
        :class="{ 'input-error': fieldErrors.name }"
        type="text"
        :placeholder="$t('form.placeholders.name')"
        v-model="formData.name"
        maxlength="60"
      />
      <input
        v-if="isClient"
        v-model="formData.phone_number"
        :class="{ 'input-error': fieldErrors.phone_number }"
        class="input__field"
        type="tel"
        v-mask="'+7 (###) ###-##-##'"
        placeholder="+7 (999) 999-99-99"
      />

      <input
        class="checkbox__field"
        type="checkbox"
        id="consent"
        v-model="formData.processing_of_personal_data"
      />
      <label class="checkbox__text" for="consent">
        <span
          class="checkbox__custom"
          :class="{ 'input-error': fieldErrors.processing_of_personal_data }"
        >
          <IconsPolicyCheckMark v-if="formData.processing_of_personal_data"/>
        </span>
        {{ $t("form.consentText") }}
        <NuxtLink
          class="checkbox__policy"
          target="_blank"
          :to="useRuntimeConfig().public.storage_host + getPolicyUrl"
        >
          {{ $t("form.consentTextLink") }}
        </NuxtLink>
      </label>

      <template v-if="isCaptchaVisible">
        <RecaptchaComponent class="form__recaptcha" @get-key="getKey"/>
      </template>

      <button
        class="form__button button button--primary"
        :class="{ 'button--primary--disabled': isButtonDisabled }"
        type="submit"
      >
        {{ $t("form.call_to_action") }}
      </button>

      <div v-if="errors.length" class="error">
        <p>{{ errors[0] }}</p>
      </div>
    </form>
  </div>
</template>

<script>
import { useFetch } from "#app";
import { useDocumentsStore } from "~/stores/documents/documents";

const apiEndpoint = "api/callback";

export default {
  name: "ModalCallback",
  data() {
    return {
      formData: {
        name: "",
        phone_number: "",
        city_id: 1,
        processing_of_personal_data: false,
        "g-recaptcha-response": "",
      },
      isCaptchaVisible: false,
      errors: [],
      isClient: false,
      isButtonDisabled: false,
      fieldErrors: {
        name: false,
        phone_number: false,
        processing_of_personal_data: false,
      },
    };
  },
  mounted() {
    this.isClient = typeof window !== "undefined";
  },
  computed: {
    getPolicyUrl() {
      return useDocumentsStore().getPolicyDocumentUrl();
    },
  },
  watch: {
    formData: {
      handler(form) {
        if (
          (form.name &&
            form.phone_number &&
            form.processing_of_personal_data) ||
          (form.name &&
            form.phone_number &&
            form.processing_of_personal_data)
        ) {
          this.isCaptchaVisible = true;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getKey(key) {
      this.formData["g-recaptcha-response"] = key;
    },
    validateForm() {
      this.errors = [];
      this.fieldErrors = {
        name: false,
        phone_number: false,
        processing_of_personal_data: false,
      };

      if (!this.formData.name) {
        this.errors.push(this.$t("form.errors.nameRequired"));
        this.fieldErrors.name = true;
        return false;
      }
      const namePattern = /^[a-zA-Zа-яА-ЯёЁ\s\-]+$/;
      if (!namePattern.test(this.formData.name)) {
        this.errors.push(this.$t("form.errors.nameInvalid"));
        this.fieldErrors.name = true;
        return false;
      }
      if (!this.formData.phone_number) {
        this.errors.push(this.$t("form.errors.phoneRequired"));
        this.fieldErrors.phone_number = true;
        return false;
      }

      const phonePattern = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
      if (!phonePattern.test(this.formData.phone_number)) {
        this.errors.push(this.$t("form.errors.phoneInvalid"));
        this.fieldErrors.phone_number = true;
        return false;
      }

      if (!this.formData.processing_of_personal_data) {
        this.errors.push(this.$t("form.errors.personalDataRequired"));
        this.fieldErrors.processing_of_personal_data = true;
        return false;
      }

      if (!this.formData["g-recaptcha-response"]) {
        this.errors.push(this.$t("form.errors.captcha"));
        this.fieldErrors.processing_of_personal_data = true;
        return false;
      }

      return true;
    },
    async submitForm() {
      const cityCookie = useCookie('active-city');
      this.formData.city_id = Number(cityCookie.value) || 0;

      if (!this.validateForm()) {
        return;
      }

      try {
        const { error } = await useFetch(useRuntimeConfig().public.api_host + apiEndpoint,
          {
            method: "POST",
            headers: { Accept: "application/json" },
            body: this.formData,
          }
        );
        if (error.value) {
          throw error.value;
        }

        this.buttonText = "Заявка отправлена";
        this.$emit("close-modal");
        this.resetForm();
      } catch (err) {
        this.$emit("close-modal-with-error");
        console.error("Error submitting form:", err);
      }
    },
    resetForm() {
      this.isCaptchaVisible = false;
      this.formData = {
        name: "",
        phone_number: "",
        city_id: 1,
        processing_of_personal_data: false,
        "g-recaptcha-response": "",
      };
      this.isCaptchaVisible = false;
      const cityCookie = useCookie('active-city');
      let cityId = Number(cityCookie.value) || 0;
      cityId
        ? (this.formData.city_id = cityId)
        : (this.formData.city_id = 1);
      this.errors = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.modal {
  position: absolute;
  top: calc(100% - 12px);
  right: 79px;
  width: 343px;
  z-index: 1000;
  padding: 40px 20px;
  background: #fff;

  @media (min-width: $screen-m) {
    box-shadow: 0 4px 30px 0 rgba(143, 143, 143, 0.3);
    width: 412px;
    padding: 50px 36px;
  }
  @media (min-width: $screen-m) and (max-width: $screen-l - 1) {
    top: calc(100% + 20px);
  }
}

.modal__title {
  font-weight: 500;
  font-size: 14px;
  line-height: 146%;
  color: #282828;
  text-align: center;

  @media (min-width: $screen-m) {
    font-size: 18px;
    line-height: 140%;
  }

  @media (min-width: $screen-l) {
    font-size: 20px;
    line-height: 132%;
  }
}

.modal__text {
  font-weight: 400;
  font-size: 14px;
  line-height: 146%;
  color: #7a7a7a;
  margin: 16px 0 24px;
  text-align: center;

  @media (min-width: $screen-m) {
    font-size: 16px;
    line-height: 152%;
  }

  @media (min-width: $screen-l) {
    font-size: 18px;
    line-height: 156%;
  }
}

.form {
  display: flex;
  flex-direction: column;
}

.form__recaptcha {
  margin-top: 24px;
  width: 100%;
}

.checkbox__field {
  display: none;
}

.checkbox__custom {
  display: flex;
  text-align: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid #b3b3b3;
  position: absolute;
  left: 0;
}

.checkbox__text {
  position: relative;
  cursor: pointer;
  padding-left: 32px;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #b3b3b3;
  margin-top: 16px;
  margin-bottom: 24px;

  @media (min-width: $screen-m) {
    font-size: 14px;
    line-height: 146%;
  }
}

.checkbox__policy {
  color: #ed1c24;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.input__field {
  height: 56px;
  border-radius: 8px;
  padding: 18px 24px;
  background: #fff;
  border: 1px solid #b3b3b3;
  font-weight: 400;
  font-size: 14px;
  line-height: 146%;
  color: #7a7a7a;

  &:not(:first-child) {
    margin-top: 12px;
  }

  @media (min-width: $screen-m) {
    font-size: 16px;
    line-height: 152%;
  }
}

.input-error {
  border: 2px solid #ed1c24;
}

.error {
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #ed1c24;

  @media (min-width: $screen-m) {
    font-size: 14px;
    line-height: 146%;
  }
}

.form__button {
  height: 48px;
  padding: 14px 48px;
  margin: 24px 0 0;

  @media (min-width: $screen-m) {
    height: 52px;
  }

  @media (min-width: $screen-l) {
    height: 56px;
  }
}
</style>
